import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';
import BrandIcon from './BrandIcon';
import CommentModal from './CommentModal';
import OperatorImageModal from './OperatorImageModal';
import MonthlyProgressTracker from './MonthlyProgressTracker';
import { getUserRole } from '../utils/rolePermissions';
import { useModal } from '../context/ModalContext';
import { useOrgBrand } from '../context/OrgBrandContext';
import { useSortableTable } from '../hooks/useSortableTable';
import { getUsableOriginalUrl } from '../utils/imageUrl';
import { formatBlogBody } from '../utils/blogFormatting';
import '../styles/ClientDashboard.css';
import '../styles/SchedulePostsPanel.css';
import CustomSelect from './CustomSelect';

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'Twitter / X' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'blog', label: 'Blog' },
  { value: 'email', label: 'Email' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'other', label: 'Other' },
];

const PLATFORM_COLORS = {
  instagram: '#E1306C',
  facebook: '#1877F2',
  twitter: '#1DA1F2',
  linkedin: '#0A66C2',
  tiktok: '#010101',
  blog: '#10B981',
  email: '#8B5CF6',
  newsletter: '#F59E0B',
  other: '#6B7280',
};

const SchedulePostsPanel = ({ user }) => {
  const modal = useModal();
  const { selectedBrand, selectedOrganization } = useOrgBrand();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('schedule_posts_active_tab') || 'ready';
  });

  useEffect(() => {
    localStorage.setItem('schedule_posts_active_tab', activeTab);
  }, [activeTab]);

  // Schedule modal
  const [scheduleModal, setScheduleModal] = useState({ open: false, draft: null });
  const [scheduleForm, setScheduleForm] = useState({ date: '', time: '' });
  const [scheduling, setScheduling] = useState(false);

  // Comment modal state
  const [commentModalState, setCommentModalState] = useState({ open: false, draftId: null, comments: [] });

  // View/Preview modal state
  const [viewingDraft, setViewingDraft] = useState(null);
  const [previewImageDimensions, setPreviewImageDimensions] = useState(null);

  useEffect(() => {
    const usableOriginal = viewingDraft && getUsableOriginalUrl(viewingDraft);
    if (usableOriginal) {
      const img = new Image();
      img.onload = () => {
        setPreviewImageDimensions({ width: img.width, height: img.height });
      };
      img.src = usableOriginal;
    } else {
      setPreviewImageDimensions(null);
    }
  }, [viewingDraft]);

  // Operator image upload/edit modal state
  const [operatorImageModal, setOperatorImageModal] = useState({ open: false, draft: null });
  const [uploadingDraft, setUploadingDraft] = useState(null);

  // Select all state
  const [selectedDrafts, setSelectedDrafts] = useState([]);

  // Extract the actual IDs we use for filtering
  const brandId = selectedBrand && selectedBrand !== 'all' ? (selectedBrand._id || selectedBrand.id) : null;
  const orgId = selectedOrganization && selectedOrganization !== 'all' ? (selectedOrganization._id || selectedOrganization.id) : null;

  useEffect(() => {
    fetchDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, orgId]);

  const { sortedData: sortedReadyDrafts, requestSort: requestReadySort, getSortClass: getReadySortClass } = useSortableTable(
    drafts.filter(d => d.status === 'approved').map(d => ({ ...d, _sortBrand: d.brand?.name || '' }))
  );
  const { sortedData: sortedScheduledDrafts, requestSort: requestSchedSort, getSortClass: getSchedSortClass } = useSortableTable(
    drafts.filter(d => d.status === 'scheduled').map(d => ({ ...d, _sortBrand: d.brand?.name || '' }))
  );

  const fetchDrafts = async () => {
    try {
      setLoading(true);

      // Build query parameters based on selected brand/org
      const params = {};
      if (selectedBrand && selectedBrand !== 'all') {
        const brandId = selectedBrand._id || selectedBrand.id;
        params.brand_id = brandId;
      }
      if (selectedOrganization && selectedOrganization !== 'all') {
        const orgId = selectedOrganization._id || selectedOrganization.id;
        params.organization_id = orgId;
      }

      const response = await axios.get('/api/content-drafts', {
        params,
        withCredentials: true
      });
      setDrafts(response.data.drafts || []);
    } catch (err) {
      console.error('Error fetching drafts:', err);
      modal.error(err.response?.data?.error || 'Failed to load drafts');
    } finally {
      setLoading(false);
    }
  };

  const handleOperatorImageConfirm = async ({ croppedImage, originalImage, cropData, backgroundColor }) => {
    const draftId = operatorImageModal.draft._id;

    try {
      setUploadingDraft(draftId);
      const formData = new FormData();

      // Add cropped image (this is what gets displayed) - preserve PNG format for transparency
      formData.append('media', new File([croppedImage], 'cropped-image.png', { type: 'image/png' }));

      // Add original image if it's a new upload
      if (originalImage) {
        formData.append('original_media', originalImage);
      }

      // Add crop data
      formData.append('crop_data', JSON.stringify(cropData));

      // Background color composited behind transparent images
      formData.append('background_color', backgroundColor || '');

      const response = await axios.post(`/api/content-drafts/${draftId}/media`, formData, {
        withCredentials: true
      });

      // Update the draft in place instead of refetching the whole list
      setDrafts(prevDrafts => prevDrafts.map(d =>
        d._id === draftId
          ? {
              ...d,
              media_url: response.data.media_url,
              media_url_original: response.data.media_url_original || d.media_url_original,
              media_crop_data: cropData,
              background_color: backgroundColor
            }
          : d
      ));

      setOperatorImageModal({ open: false, draft: null });
      modal.success('Image updated successfully');
    } catch (err) {
      modal.error(err.response?.data?.error || 'Failed to upload media');
    } finally {
      setUploadingDraft(null);
    }
  };

  const openScheduleModal = (draft) => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    now.setHours(now.getHours() + 1);
    const dateStr = now.toISOString().slice(0, 10);
    const timeStr = now.toTimeString().slice(0, 5);
    setScheduleForm({ date: dateStr, time: timeStr });
    setScheduleModal({ open: true, draft });
  };

  const handleSchedule = async () => {
    if (!scheduleForm.date || !scheduleForm.time) return;
    try {
      setScheduling(true);
      const scheduledDate = `${scheduleForm.date}T${scheduleForm.time}`;
      await axios.post(
        `/api/content-drafts/${scheduleModal.draft._id}/schedule`,
        { scheduled_date: scheduledDate },
        { withCredentials: true }
      );
      setScheduleModal({ open: false, draft: null });
      fetchDrafts();
    } catch (err) {
      modal.error(err.response?.data?.error || 'Failed to schedule post');
    } finally {
      setScheduling(false);
    }
  };


  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  const formatCommentTimestamp = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `${month}/${day} ${hours}:${minutesStr} ${ampm}`;
  };

  const getContentTypeBadge = (type) => {
    const colors = {
      social: '#3B82F6', blog: '#10B981', video: '#EF4444',
      podcast: '#8B5CF6', newsletter: '#F59E0B'
    };
    return (
      <span className="sp-type-badge" style={{ backgroundColor: colors[type] || '#6B7280' }}>
        {type || '-'}
      </span>
    );
  };

  const getPlatformBadge = (platform) => {
    if (!platform) return '-';
    const label = PLATFORMS.find(p => p.value === platform)?.label || platform;
    return (
      <span className="sp-platform-badge" style={{ backgroundColor: PLATFORM_COLORS[platform] || '#6B7280' }}>
        {label}
      </span>
    );
  };

  const getPreviewImageStyle = (draft) => {
    if (!draft.media_url || !draft.media_crop_data || !previewImageDimensions) {
      return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: '100%',
        maxHeight: '100%',
      };
    }

    const cropData = draft.media_crop_data;

    const containerAspect = 4 / 3;
    const imageAspect = previewImageDimensions.width / previewImageDimensions.height;

    const editorWidth = 450;
    const editorHeight = editorWidth / containerAspect;

    const cropPercentX = (cropData.cropPosition.x / editorWidth) * 100;
    const cropPercentY = (cropData.cropPosition.y / editorHeight) * 100;

    return {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: `translate(calc(-50% + ${cropPercentX}%), calc(-50% + ${cropPercentY}%)) scale(${cropData.zoom})`,
      transformOrigin: 'center',
      width: imageAspect > containerAspect ? 'auto' : '100%',
      height: imageAspect > containerAspect ? '100%' : 'auto',
      maxWidth: 'none',
      maxHeight: 'none',
    };
  };

  const renderContentPreviewModal = () => {
    if (!viewingDraft) return null;
    const draft = viewingDraft;
    const brandName = draft.brand?.name || 'Brand';
    const brandLogo = draft.brand?.icon_url;
    const isSocial = draft.content_type === 'social';
    const isBlog = draft.content_type === 'blog';
    const dateStr = formatDate(draft.client_decision_at || draft.created_at);

    const extractHashtags = (text) => (text || '').match(/#\w+/g) || [];
    const hashtags = [...new Set([
      ...extractHashtags(draft.production_notes || ''),
      ...extractHashtags(draft.body || '')
    ])];
    const cleanBody = (draft.body || '').replace(/#\w+/g, '').trim();

    return (
      <div className="mock-modal-overlay" onClick={() => setViewingDraft(null)}>
        <div className="mock-modal" onClick={(e) => e.stopPropagation()}>
          <div className="mock-modal-header">
            <h3>Content Preview</h3>
            <button className="modal-close-btn" onClick={() => setViewingDraft(null)} aria-label="Close" />
          </div>

          <div className="mock-modal-body">
            {isSocial ? (
              <div className="mock-social-post">
                <div className="mock-post-header">
                  <div className="mock-author-info">
                    {brandLogo ? (
                      <BrandIcon src={brandLogo} alt={brandName} className="mock-brand-avatar" />
                    ) : (
                      <div className="mock-brand-initial">{brandName.charAt(0).toUpperCase()}</div>
                    )}
                    <div>
                      <div className="mock-brand-name">{brandName}</div>
                      <div className="mock-post-meta">{dateStr} · <Icon name="link" size="0.9rem" /></div>
                    </div>
                  </div>
                  <span className="mock-type-tag mock-type-social">SOCIAL</span>
                </div>
                <div className="mock-post-body">
                  {formatBlogBody(cleanBody || draft.body || '').map(block => (
                    block.type === 'heading'
                      ? <h4 key={block.key} className="mock-blog-subheading">{block.text}</h4>
                      : <p key={block.key} className="mock-post-paragraph">{block.text}</p>
                  ))}
                </div>
                {hashtags.length > 0 && (
                  <div className="mock-hashtags">
                    {hashtags.map((h, i) => <span key={i} className="mock-hashtag">{h} </span>)}
                  </div>
                )}
                {draft.media_url && (
                  <div
                    className="mock-media-wrapper"
                    style={draft.background_color && draft.background_color !== 'transparent' ? { backgroundColor: draft.background_color } : undefined}
                  >
                    {draft.media_crop_data && getUsableOriginalUrl(draft) ? (
                      <div style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden'
                      }}>
                        <img
                          src={getUsableOriginalUrl(draft)}
                          alt="Preview"
                          style={getPreviewImageStyle(draft)}
                        />
                      </div>
                    ) : (
                      <img src={draft.media_url} alt="Content media" className="mock-post-image" />
                    )}
                  </div>
                )}
                <div className="mock-social-actions">
                  <span className="mock-social-btn" style={{ color: '#E1306C' }}><Icon name="heart" size="0.9rem" /> Like</span>
                  <span className="mock-social-btn"><Icon name="comment-empty" size="0.9rem" /> Comment</span>
                  <span className="mock-social-btn"><Icon name="share" size="0.9rem" /> Share</span>
                  <span className="mock-social-btn"><Icon name="paper-plane-empty" size="0.9rem" /> Send</span>
                </div>
              </div>
            ) : isBlog ? (
              <div className="mock-blog-post">
                <div className="mock-blog-url-bar">
                  <span className="mock-blog-url">
                    {draft.call_to_action || `https://blog.example.com/${(draft.headline || draft.title || 'post').toLowerCase().replace(/\s+/g, '-')}`}
                  </span>
                  <span className="mock-type-tag mock-type-blog">BLOG</span>
                </div>
                {draft.media_url && (
                  <div className="mock-blog-image-wrapper">
                    <img src={draft.media_url} alt="Blog header" className="mock-blog-image" />
                  </div>
                )}
                {draft.headline && <h2 className="mock-blog-title">{draft.headline}</h2>}
                <div className="mock-blog-date">{dateStr}</div>
                <div className="mock-blog-body">
                  {formatBlogBody(draft.body).map(block => (
                    block.type === 'heading'
                      ? <h4 key={block.key} className="mock-blog-subheading">{block.text}</h4>
                      : <p key={block.key} className="mock-blog-paragraph">{block.text}</p>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mock-generic-post">
                <div className="mock-post-header">
                  <div className="mock-author-info">
                    {brandLogo ? (
                      <BrandIcon src={brandLogo} alt={brandName} className="mock-brand-avatar" />
                    ) : (
                      <div className="mock-brand-initial">{brandName.charAt(0).toUpperCase()}</div>
                    )}
                    <div>
                      <div className="mock-brand-name">{brandName}</div>
                      <div className="mock-post-meta">{dateStr}</div>
                    </div>
                  </div>
                  <span className="mock-type-tag" style={{ backgroundColor: getContentTypeBadge(draft.content_type).props.style.backgroundColor }}>
                    {(draft.content_type || 'CONTENT').toUpperCase()}
                  </span>
                </div>
                {draft.headline && <div className="mock-post-title">{draft.headline}</div>}
                <div className="mock-post-body">
                  {formatBlogBody(draft.body).map(block => (
                    block.type === 'heading'
                      ? <h4 key={block.key} className="mock-blog-subheading">{block.text}</h4>
                      : <p key={block.key} className="mock-post-paragraph">{block.text}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const readyDrafts = sortedReadyDrafts;
  const scheduledDrafts = sortedScheduledDrafts;

  const toggleSelectAll = (draftsList) => {
    const draftIds = draftsList.map(d => d._id);
    const allSelected = draftIds.length > 0 && draftIds.every(id => selectedDrafts.includes(id));
    if (allSelected) {
      setSelectedDrafts([]);
    } else {
      setSelectedDrafts(draftIds);
    }
  };

  const toggleDraftSelect = (draftId) => {
    setSelectedDrafts(prev =>
      prev.includes(draftId)
        ? prev.filter(id => id !== draftId)
        : [...prev, draftId]
    );
  };

  if (loading) {
    return (
      <div className="sp-container">
        <div className="sp-content-wrapper">
          <div className="sp-loading">
            <div className="sp-spinner"></div>
            <p>Loading posts...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sp-container">
      <div className="sp-content-wrapper">
      {/* Tabs */}
      <div className="sp-tabs-container">
        <button
          className={`sp-tab ${activeTab === 'ready' ? 'active' : ''}`}
          onClick={() => setActiveTab('ready')}
        >
          <Icon name="clock" size="1rem" />
          Ready to Schedule
          {readyDrafts.length > 0 && (
            <span className="sp-tab-badge">{readyDrafts.length}</span>
          )}
        </button>
        <button
          className={`sp-tab ${activeTab === 'scheduled' ? 'active' : ''}`}
          onClick={() => setActiveTab('scheduled')}
        >
          <Icon name="calendar" size="1rem" />
          Scheduled
          {scheduledDrafts.length > 0 && (
            <span className="sp-tab-badge">{scheduledDrafts.length}</span>
          )}
        </button>
      </div>

      <MonthlyProgressTracker brandId={brandId} />

      {/* Ready to Schedule Tab */}
      {activeTab === 'ready' && (
        <div className="sp-section">
          <div className="sp-header-controls">
            <button className="sp-btn-refresh" onClick={fetchDrafts}>Refresh</button>
          </div>

          {readyDrafts.length === 0 ? (
            <div className="sp-empty">
              <Icon name="calendar" size="2.5rem" />
              <p>No approved content waiting to be scheduled.</p>
            </div>
          ) : (
            <div className="sp-table-wrapper">
              <table className="sp-table">
                <thead>
                  <tr>
                    <th className={getReadySortClass('_sortBrand')} onClick={() => requestReadySort('_sortBrand')}>Brand</th>
                    <th style={{ width: '90px' }}>Image</th>
                    <th className={getReadySortClass('headline')} onClick={() => requestReadySort('headline')}>Title</th>
                    <th>Body Preview</th>
                    <th style={{ width: '100px' }} className={getReadySortClass('content_type')} onClick={() => requestReadySort('content_type')}>Type</th>
                    <th style={{ width: '130px' }} className={getReadySortClass('client_decision_at')} onClick={() => requestReadySort('client_decision_at')}>Client Approved</th>
                    <th style={{ width: '110px' }}>Actions</th>
                    <th style={{ width: '80px' }}>Comments</th>
                    <th style={{ width: '80px' }}>View</th>
                  </tr>
                </thead>
                <tbody>
                  {readyDrafts.map(draft => (
                    <tr key={draft._id} className="sp-draft-row">
                      <td className="sp-brand-cell">
                        {draft.brand?.icon_url ? (
                          <BrandIcon src={draft.brand.icon_url} alt={draft.brand?.name} className="sp-brand-logo-small" />
                        ) : (
                          <div className="sp-brand-placeholder-small">
                            <span>{(draft.brand?.name || 'B').charAt(0).toUpperCase()}</span>
                          </div>
                        )}
                      </td>
                      <td className="rc-upload-cell">
                        <button
                          className={`rc-upload-btn${draft.media_url ? ' has-media' : ''}`}
                          title={draft.media_url ? 'Replace media' : 'Upload media'}
                          onClick={() => setOperatorImageModal({ open: true, draft })}
                          disabled={uploadingDraft === draft._id}
                        >
                          <Icon
                            name={uploadingDraft === draft._id ? 'clock' : 'picture'}
                            size="1rem"
                          />
                          {draft.media_url && (
                            <span className="rc-upload-check">
                              <Icon name="ok" size="0.7rem" />
                            </span>
                          )}
                        </button>
                      </td>
                      <td className="sp-title-cell">
                        <div className="sp-draft-title">{draft.title || draft.headline || 'Untitled'}</div>
                      </td>
                      <td className="sp-body-cell">
                        <div className="sp-body-preview">{draft.body || '-'}</div>
                      </td>
                      <td>{getContentTypeBadge(draft.content_type)}</td>
                      <td className="sp-date-cell">{formatDate(draft.client_decision_at || draft.updated_at)}</td>
                      <td className="sp-action-cell">
                        <button
                          className="sp-schedule-btn"
                          onClick={() => openScheduleModal(draft)}
                        >
                          <Icon name="calendar" size="1rem" />
                          Schedule
                        </button>
                      </td>
                      <td className="sp-comment-cell">
                        <button
                          className="sp-comment-icon-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setCommentModalState({
                              open: true,
                              draftId: draft._id,
                              comments: draft.comments || []
                            });
                          }}
                          title="Add comment"
                          type="button"
                        >
                          <Icon name="comment-empty" size="1.5rem" />
                          {draft.comments && draft.comments.length > 0 && (
                            <span className="sp-comment-count-badge">{draft.comments.length}</span>
                          )}
                        </button>
                      </td>
                      <td className="sp-view-cell">
                        <button
                          className="sp-view-btn"
                          onClick={() => setViewingDraft(draft)}
                          title="View content preview"
                        >
                          <Icon name="go-to-link" size="1.75rem" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Scheduled Tab */}
      {activeTab === 'scheduled' && (
        <div className="sp-section">
          <div className="sp-header-controls">
            <button className="sp-btn-refresh" onClick={fetchDrafts}>Refresh</button>
          </div>

          {scheduledDrafts.length === 0 ? (
            <div className="sp-empty">
              <Icon name="calendar" size="2.5rem" />
              <p>No scheduled posts yet.</p>
            </div>
          ) : (
            <div className="sp-table-wrapper">
              <table className="sp-table">
                <thead>
                  <tr>
                    <th className={getSchedSortClass('_sortBrand')} onClick={() => requestSchedSort('_sortBrand')}>Brand</th>
                    <th style={{ width: '70px' }}>Image</th>
                    <th className={getSchedSortClass('headline')} onClick={() => requestSchedSort('headline')}>Title</th>
                    <th>Body Preview</th>
                    <th style={{ width: '120px' }} className={getSchedSortClass('content_type')} onClick={() => requestSchedSort('content_type')}>Type</th>
                    <th style={{ width: '180px' }} className={getSchedSortClass('scheduled_date')} onClick={() => requestSchedSort('scheduled_date')}>Scheduled For</th>
                    <th style={{ width: '80px' }}>Comments</th>
                    <th style={{ width: '80px' }}>View</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduledDrafts.map(draft => (
                    <tr key={draft._id} className="sp-draft-row">
                      <td className="sp-brand-cell">
                        {draft.brand?.icon_url ? (
                          <BrandIcon src={draft.brand.icon_url} alt={draft.brand?.name} className="sp-brand-logo-small" />
                        ) : (
                          <div className="sp-brand-placeholder-small">
                            <span>{(draft.brand?.name || 'B').charAt(0).toUpperCase()}</span>
                          </div>
                        )}
                      </td>
                      <td className="sp-image-cell">
                        {draft.media_url ? (
                          <button
                            className="sp-image-thumb-btn"
                            onClick={() => setViewingDraft(draft)}
                            title="View image"
                            type="button"
                          >
                            <img src={draft.media_url} alt="Content media" className="sp-image-thumb" />
                          </button>
                        ) : (
                          <span className="sp-image-placeholder">
                            <Icon name="picture" size="1.1rem" />
                          </span>
                        )}
                      </td>
                      <td className="sp-title-cell">
                        <div className="sp-draft-title">{draft.title || draft.headline || 'Untitled'}</div>
                      </td>
                      <td className="sp-body-cell">
                        <div className="sp-body-preview">{draft.body || '-'}</div>
                      </td>
                      <td>{getContentTypeBadge(draft.content_type)}</td>
                      <td className="sp-date-cell">{formatDateTime(draft.scheduled_date)}</td>
                      <td className="sp-comment-cell">
                        <button
                          className="sp-comment-icon-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setCommentModalState({
                              open: true,
                              draftId: draft._id,
                              comments: draft.comments || []
                            });
                          }}
                          title="Add comment"
                          type="button"
                        >
                          <Icon name="comment-empty" size="1.5rem" />
                          {draft.comments && draft.comments.length > 0 && (
                            <span className="sp-comment-count-badge">{draft.comments.length}</span>
                          )}
                        </button>
                      </td>
                      <td className="sp-view-cell">
                        <button
                          className="sp-view-btn"
                          onClick={() => setViewingDraft(draft)}
                          title="View content preview"
                        >
                          <Icon name="go-to-link" size="1.75rem" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Schedule Modal */}
      {scheduleModal.open && (
        <div className="sp-modal-overlay" onClick={() => setScheduleModal({ open: false, draft: null })}>
          <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-header">
              <h3>Schedule Post</h3>
              <button className="modal-close-btn" onClick={() => setScheduleModal({ open: false, draft: null })} aria-label="Close" />
            </div>
            <div className="sp-modal-body">
              <div className="sp-modal-draft-title">
                {scheduleModal.draft?.headline || scheduleModal.draft?.title || 'Untitled'}
              </div>

              <div className="sp-form-row">
                <div className="sp-form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={scheduleForm.date}
                    onChange={(e) => setScheduleForm(prev => ({ ...prev, date: e.target.value }))}
                    className="sp-input"
                    min={new Date().toISOString().slice(0, 10)}
                  />
                </div>
                <div className="sp-form-group">
                  <label>Time *</label>
                  <input
                    type="time"
                    value={scheduleForm.time}
                    onChange={(e) => setScheduleForm(prev => ({ ...prev, time: e.target.value }))}
                    className="sp-input"
                  />
                </div>
              </div>

              <div className="sp-modal-actions">
                <button
                  className="sp-btn-cancel"
                  onClick={() => setScheduleModal({ open: false, draft: null })}
                >
                  Cancel
                </button>
                <button
                  className="sp-btn-schedule-confirm"
                  onClick={handleSchedule}
                  disabled={scheduling || !scheduleForm.date || !scheduleForm.time}
                >
                  {scheduling ? 'Scheduling...' : 'Confirm Schedule'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Preview Modal */}
      {renderContentPreviewModal()}

      {/* Operator Image Upload/Edit Modal */}
      <OperatorImageModal
        isOpen={operatorImageModal.open}
        onClose={() => setOperatorImageModal({ open: false, draft: null })}
        onConfirm={handleOperatorImageConfirm}
        existingMediaUrl={operatorImageModal.draft?.media_url}
        existingOriginalUrl={operatorImageModal.draft?.media_url_original}
        existingCropData={operatorImageModal.draft?.media_crop_data}
        existingBackgroundColor={operatorImageModal.draft?.background_color}
        brandName={operatorImageModal.draft?.brand?.name}
        brandLogo={operatorImageModal.draft?.brand?.icon_url}
        draftTitle={operatorImageModal.draft?.content_type === 'social' ? null : (operatorImageModal.draft?.headline || operatorImageModal.draft?.title)}
        draftBody={operatorImageModal.draft?.body}
        contentType={operatorImageModal.draft?.content_type}
        draftDate={formatDate(operatorImageModal.draft?.client_decision_at || operatorImageModal.draft?.created_at)}
      />

      {/* Comment Modal */}
      <CommentModal
        isOpen={commentModalState.open}
        onClose={() => setCommentModalState({ open: false, draftId: null, comments: [] })}
        itemId={commentModalState.draftId}
        itemType="content-draft"
        comments={commentModalState.comments}
        user={user}
        onCommentAdded={(newComment) => {
          setDrafts(prev => prev.map(draft =>
            draft._id === commentModalState.draftId
              ? { ...draft, comments: [...(draft.comments || []), newComment] }
              : draft
          ));
        }}
        onCommentDeleted={(commentId) => {
          setDrafts(prev => prev.map(draft =>
            draft._id === commentModalState.draftId
              ? { ...draft, comments: (draft.comments || []).filter(c => (c._id || c.id) !== commentId) }
              : draft
          ));
        }}
      />
      </div>
    </div>
  );
};

export default SchedulePostsPanel;
